/**
 * Database storage API - replaces localStorage for beta keys, waitlist, and app settings.
 * All paths go through the backend (Supabase Edge Function).
 */

import { projectId, publicAnonKey } from '/utils/supabase/info';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

/** Set when AdminRoute renders (unified auth). Cleared on sign out. */
let currentAdminAccessToken: string | null = null;
export function setAdminAccessTokenForApi(token: string | null) {
  currentAdminAccessToken = token;
}

/**
 * Headers for Supabase Edge routes that use validateUserOrAdmin (anon Authorization + user/session/admin).
 * Optional userToken overrides localStorage when callers already hold the session JWT (e.g. EI coach).
 */
export function getAuthHeadersForProtectedApi(opts?: { userToken?: string | null }): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
    apikey: publicAnonKey,
  };
  const fromOpts = opts?.userToken?.trim();
  const fromMemory = currentAdminAccessToken?.trim();
  const fromStorage =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tension_access_token') : null;
  const userToken = fromOpts || fromMemory || fromStorage;
  if (userToken) {
    headers['X-User-Token'] = userToken;
  }
  const sessionLocal = typeof localStorage !== 'undefined' ? localStorage.getItem('tension_admin_session') : null;
  const sessionScoped =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('tension_admin_session') : null;
  const session = sessionLocal || sessionScoped;
  if (session) {
    try {
      const parsed = JSON.parse(session);
      const sessionToken =
        typeof parsed?.sessionToken === 'string'
          ? parsed.sessionToken
          : typeof parsed?.token === 'string'
            ? parsed.token
            : '';
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      if (typeof parsed?.role === 'string' && parsed.role.trim().length > 0) {
        headers['X-Admin-Role'] = parsed.role.trim();
      }
    } catch (_) {}
  }
  return headers;
}

/** Admin API headers. Uses unified auth token when set, else validated admin session token. */
export function getAdminHeaders(_opts?: boolean): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeadersForProtectedApi(),
  };
  return headers;
}

/** Get current user role (for unified auth). Returns backend role when available. */
export async function getCurrentUserRole(accessToken: string | null): Promise<{
  role: string;
  isAdmin: boolean;
  source: 'api' | 'fallback_error';
}> {
  if (!accessToken) return { role: 'user', isAdmin: false, source: 'fallback_error' };
  try {
    const res = await fetch(`${API_BASE}/user/role`, {
      headers: {
        'Authorization': `Bearer ${publicAnonKey}`,
        'X-User-Token': accessToken,
      },
    });
    const data = (await res.json()) as { role?: string; isAdmin?: boolean };
    if (!res.ok) {
      console.warn('[getCurrentUserRole] HTTP', res.status, data);
      return { role: 'user', isAdmin: false, source: 'fallback_error' };
    }
    return {
      role: typeof data.role === 'string' ? data.role : 'user',
      isAdmin: Boolean(data.isAdmin),
      source: 'api',
    };
  } catch (e) {
    console.warn('[getCurrentUserRole] failed', e);
    return { role: 'user', isAdmin: false, source: 'fallback_error' };
  }
}

// --- Beta keys (database) ---

export interface BetaKeyRow {
  id: string;
  key: string;
  status: string;
  used_by?: string | null;
  used_at?: string | null;
  note?: string | null;
  created_by?: string | null;
  created_at?: string | null;
}

export async function getBetaKeys(): Promise<BetaKeyRow[]> {
  const res = await fetch(`${API_BASE}/beta/keys`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.keys ?? [];
}

export async function validateBetaKeyForEmail(
  key: string,
  email: string,
  claim = false,
): Promise<{ valid: boolean; reason?: string }> {
  const res = await fetch(`${API_BASE}/beta/keys/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${publicAnonKey}` },
    body: JSON.stringify({ key: key.trim(), email: email.trim().toLowerCase(), claim }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to validate beta key');
  return { valid: Boolean(data.valid), reason: data.reason };
}

export async function addBetaKeys(keys: string[], note?: string, createdBy?: string): Promise<BetaKeyRow[]> {
  const res = await fetch(`${API_BASE}/beta/keys`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ keys, note, created_by: createdBy }),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.keys ?? [];
}

export async function deleteBetaKey(key: string): Promise<void> {
  const res = await fetch(`${API_BASE}/beta/keys/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function updateBetaKeyStatus(key: string, status: string): Promise<void> {
  const res = await fetch(`${API_BASE}/beta/keys/${encodeURIComponent(key)}`, {
    method: 'PATCH',
    headers: getAdminHeaders(),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(await res.text());
}

// --- Waitlist (database) ---

export interface WaitlistRow {
  id: string;
  email: string;
  name?: string | null;
  position: number;
  status?: string | null;
  referral_code?: string | null;
  referrals?: number | null;
  joined_at?: string | null;
}

export interface WaitlistEntryWithMeta extends WaitlistRow {
  joinedAt?: string | null;
  status?: string;
  betaKey?: string;
  sentAt?: string;
  notes?: string;
}

export async function getWaitlist(): Promise<WaitlistEntryWithMeta[]> {
  const res = await fetch(`${API_BASE}/waitlist`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.list ?? [];
}

export async function setWaitlistEntryMeta(
  email: string,
  updates: { status?: string; notes?: string; betaKey?: string; sentAt?: string }
): Promise<void> {
  const res = await fetch(`${API_BASE}/waitlist/meta`, {
    method: 'PATCH',
    headers: getAdminHeaders(),
    body: JSON.stringify({ email, ...updates }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function setWaitlistMeta(meta: Record<string, { status?: string; notes?: string; betaKey?: string; sentAt?: string }>): Promise<void> {
  const res = await fetch(`${API_BASE}/waitlist/meta`, {
    method: 'PUT',
    headers: getAdminHeaders(),
    body: JSON.stringify({ meta }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function deleteWaitlistEntryByEmail(email: string): Promise<void> {
  const res = await fetch(`${API_BASE}/waitlist/entry`, {
    method: 'DELETE',
    headers: getAdminHeaders(),
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await res.text());
}

export interface BetaReminderSendResult {
  success: boolean;
  dryRun?: boolean;
  firstReminderOnly?: boolean;
  eligibleCount?: number;
  totalWaitlist?: number;
  skippedAlreadyReminded?: number;
  preview?: Array<{ email: string; status: string; sentAt?: string | null }>;
  sentCount?: number;
  failedCount?: number;
  message?: string;
}

export async function sendBetaRedemptionReminders(opts?: {
  dryRun?: boolean;
  limit?: number;
  minHoursSinceInvite?: number;
  firstReminderOnly?: boolean;
  subject?: string;
}): Promise<BetaReminderSendResult> {
  const res = await fetch(`${API_BASE}/beta/reminders/send`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({
      dryRun: opts?.dryRun ?? false,
      limit: opts?.limit ?? 500,
      minHoursSinceInvite: opts?.minHoursSinceInvite ?? 24,
      firstReminderOnly: opts?.firstReminderOnly ?? false,
      subject: opts?.subject,
    }),
  });

  const data = (await res.json().catch(() => ({}))) as BetaReminderSendResult & { error?: string };
  if (!res.ok) throw new Error(data.error || 'Failed to send beta redemption reminders');
  return data;
}

export interface BetaReminderCampaign {
  id: string;
  sentAt: string;
  subject: string;
  firstReminderOnly: boolean;
  minHoursSinceInvite: number;
  limit: number;
  sentCount: number;
  failedCount: number;
  skippedAlreadyReminded: number;
  sampleRecipients: string[];
}

export async function getBetaReminderCampaigns(limit = 20): Promise<BetaReminderCampaign[]> {
  const res = await fetch(`${API_BASE}/beta/reminders/campaigns?limit=${Math.max(1, Math.min(100, limit))}`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { campaigns?: BetaReminderCampaign[] };
  return Array.isArray(data.campaigns) ? data.campaigns : [];
}

/** Admin: fetch all beta feedback (from /feedback submissions). */
export interface AdminFeedbackItem {
  id: string;
  type: string;
  message: string;
  userEmail: string;
  userId: string;
  status?: 'new' | 'in_progress' | 'completed';
  deleted?: boolean;
  adminResponse?: string;
  adminResponseAt?: string;
  respondedBy?: string;
  adminNotes?: string;
  notifiedUser?: boolean;
  userAgent?: string;
  url?: string;
  timestamp?: string;
  createdAt: string;
}

export async function getAdminFeedback(): Promise<AdminFeedbackItem[]> {
  const res = await fetch(`${API_BASE}/admin/feedback`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { success?: boolean; feedback?: AdminFeedbackItem[]; count?: number };
  return Array.isArray(data.feedback) ? data.feedback : [];
}

export async function updateAdminFeedback(
  id: string,
  patch: {
    status?: 'new' | 'in_progress' | 'completed';
    adminNotes?: string;
    deleted?: boolean;
  },
): Promise<AdminFeedbackItem> {
  const res = await fetch(`${API_BASE}/admin/feedback/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: getAdminHeaders(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { feedback?: AdminFeedbackItem };
  if (!data.feedback) throw new Error('Failed to update feedback');
  return data.feedback;
}

export async function respondToAdminFeedback(
  id: string,
  response: string,
  markCompleted = true,
): Promise<{ feedback: AdminFeedbackItem; notifiedUser: boolean }> {
  const res = await fetch(`${API_BASE}/admin/feedback/${encodeURIComponent(id)}/respond`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ response, markCompleted }),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { feedback?: AdminFeedbackItem; notifiedUser?: boolean };
  if (!data.feedback) throw new Error('Failed to send response');
  return { feedback: data.feedback, notifiedUser: Boolean(data.notifiedUser) };
}

export async function deleteAdminFeedback(id: string): Promise<AdminFeedbackItem> {
  const res = await fetch(`${API_BASE}/admin/feedback/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { feedback?: AdminFeedbackItem };
  if (!data.feedback) throw new Error('Failed to delete feedback');
  return data.feedback;
}

export interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  summary: string;
  highlights: string[];
  bugFixes: string[];
  releasedAt: string;
  websitePublished?: boolean;
  androidPublished?: boolean;
  iosPublished?: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function getAdminChangelogEntries(): Promise<ChangelogEntry[]> {
  const res = await fetch(`${API_BASE}/admin/changelog`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { entries?: ChangelogEntry[] };
  return Array.isArray(data.entries) ? data.entries : [];
}

export async function upsertAdminChangelogEntry(
  payload: Partial<ChangelogEntry> & {
    version: string;
    title: string;
    summary: string;
    highlights?: string[];
    bugFixes?: string[];
  },
): Promise<ChangelogEntry> {
  const res = await fetch(`${API_BASE}/admin/changelog`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { entry?: ChangelogEntry };
  if (!data.entry) throw new Error('Failed to save changelog entry');
  return data.entry;
}

export async function getAdminChangelogExport(
  id: string,
  target: 'website' | 'google_play' | 'apple_store' | 'json',
): Promise<{ target: string; payload: string }> {
  const res = await fetch(`${API_BASE}/admin/changelog/${encodeURIComponent(id)}/export?target=${target}`, {
    headers: getAdminHeaders(),
  });
  if (!res.ok) throw new Error(await res.text());
  return (await res.json()) as { target: string; payload: string };
}

/** Admin: Resend email inbox — list received emails. */
export interface ResendEmailListItem {
  id: string;
  to: string[];
  from: string;
  created_at: string;
  subject: string;
  attachments?: Array<{ id: string; filename: string; size?: number }>;
}

export async function getAdminEmailsReceived(): Promise<{ data: ResendEmailListItem[]; has_more?: boolean }> {
  const res = await fetch(`${API_BASE}/admin/emails/received`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { data?: ResendEmailListItem[]; has_more?: boolean };
  return { data: Array.isArray(data.data) ? data.data : [], has_more: data.has_more };
}

export interface AdminEmailHealth {
  ok: boolean;
  inboxCount: number;
  receivedCount: number;
  lastInboundAt: string | null;
  webhookConfigured: boolean;
  resendConfigured: boolean;
  deliveryMode?: 'resend_live' | 'local_fallback';
  inboundPollingAvailable?: boolean;
  resendPullStatus?: {
    lastPulledAt: string | null;
    lastImported: number;
    lastSkipped: number;
    lastError?: string;
  };
}

export async function getAdminEmailHealth(): Promise<AdminEmailHealth> {
  const res = await fetch(`${API_BASE}/admin/emails/health`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as Partial<AdminEmailHealth>;
  return {
    ok: data.ok === true,
    inboxCount: typeof data.inboxCount === 'number' ? data.inboxCount : 0,
    receivedCount: typeof data.receivedCount === 'number' ? data.receivedCount : 0,
    lastInboundAt: typeof data.lastInboundAt === 'string' ? data.lastInboundAt : null,
    webhookConfigured: data.webhookConfigured === true,
    resendConfigured: data.resendConfigured === true,
    deliveryMode: data.deliveryMode === 'resend_live' || data.deliveryMode === 'local_fallback'
      ? data.deliveryMode
      : undefined,
    inboundPollingAvailable: data.inboundPollingAvailable === true,
    resendPullStatus:
      data.resendPullStatus && typeof data.resendPullStatus === 'object'
        ? {
            lastPulledAt:
              typeof data.resendPullStatus.lastPulledAt === 'string'
                ? data.resendPullStatus.lastPulledAt
                : null,
            lastImported:
              typeof data.resendPullStatus.lastImported === 'number'
                ? data.resendPullStatus.lastImported
                : 0,
            lastSkipped:
              typeof data.resendPullStatus.lastSkipped === 'number'
                ? data.resendPullStatus.lastSkipped
                : 0,
            lastError:
              typeof data.resendPullStatus.lastError === 'string'
                ? data.resendPullStatus.lastError
                : '',
          }
        : undefined,
  };
}

export interface AdminEmailWebhookEvent {
  at: string;
  status: 'accepted' | 'rejected' | 'ignored' | 'synthetic';
  method: string;
  reason: string;
  eventType?: string;
  emailId?: string;
}

export interface AdminEmailDiagnostics {
  health: AdminEmailHealth;
  webhookMode: 'strict' | 'fallback-secret' | 'compat' | 'open';
  lastWebhookEvents: AdminEmailWebhookEvent[];
  recentSubjects: Array<{ id: string; subject: string; created_at: string; from: string }>;
  expectedWebhookUrls: string[];
  recommendedWebhookUrl: string;
  sendConfig: {
    defaultFrom: string;
    waitlistFrom: string;
    waitlistReplyTo: string;
    appBaseUrl: string;
  };
}

export async function getAdminEmailDiagnostics(): Promise<AdminEmailDiagnostics> {
  const res = await fetch(`${API_BASE}/admin/emails/diagnostics`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as Partial<AdminEmailDiagnostics>;
  return {
    health: {
      ok: data.health?.ok === true,
      inboxCount: typeof data.health?.inboxCount === 'number' ? data.health.inboxCount : 0,
      receivedCount: typeof data.health?.receivedCount === 'number' ? data.health.receivedCount : 0,
      lastInboundAt: typeof data.health?.lastInboundAt === 'string' ? data.health.lastInboundAt : null,
      webhookConfigured: data.health?.webhookConfigured === true,
      resendConfigured: data.health?.resendConfigured === true,
    },
    webhookMode:
      data.webhookMode === 'strict' ||
      data.webhookMode === 'fallback-secret' ||
      data.webhookMode === 'compat' ||
      data.webhookMode === 'open'
        ? data.webhookMode
        : 'open',
    lastWebhookEvents: Array.isArray(data.lastWebhookEvents) ? data.lastWebhookEvents : [],
    recentSubjects: Array.isArray(data.recentSubjects) ? data.recentSubjects : [],
    expectedWebhookUrls: Array.isArray((data as any)?.health?.expectedWebhookUrls)
      ? (data as any).health.expectedWebhookUrls.filter((v: unknown) => typeof v === 'string')
      : [],
    recommendedWebhookUrl: typeof (data as any)?.health?.recommendedWebhookUrl === 'string'
      ? (data as any).health.recommendedWebhookUrl
      : '',
    sendConfig: {
      defaultFrom: typeof (data as any)?.sendConfig?.defaultFrom === 'string' ? (data as any).sendConfig.defaultFrom : '',
      waitlistFrom: typeof (data as any)?.sendConfig?.waitlistFrom === 'string' ? (data as any).sendConfig.waitlistFrom : '',
      waitlistReplyTo: typeof (data as any)?.sendConfig?.waitlistReplyTo === 'string' ? (data as any).sendConfig.waitlistReplyTo : '',
      appBaseUrl: typeof (data as any)?.sendConfig?.appBaseUrl === 'string' ? (data as any).sendConfig.appBaseUrl : '',
    },
  };
}

export interface AdminEmailAutomationRule {
  id: string;
  name: string;
  enabled: boolean;
  priority: number;
  match: {
    fromIncludes?: string[];
    domainIncludes?: string[];
    subjectIncludes?: string[];
    bodyIncludes?: string[];
    categoryIs?: string[];
    spamLikelihoodIs?: Array<'low' | 'medium' | 'high'>;
    olderThanHours?: number | null;
  };
  action: {
    archive?: boolean;
    trash?: boolean;
    setCategory?: string;
    setSpamLikelihood?: 'low' | 'medium' | 'high' | '';
    appendReason?: string;
  };
}

export interface AdminEmailAutomationConfig {
  rules: AdminEmailAutomationRule[];
  slaHours: number;
}

export interface AdminEmailAutomationLogEntry {
  at: string;
  kind: string;
  [key: string]: unknown;
}

export async function getAdminEmailAutomationConfig(): Promise<AdminEmailAutomationConfig> {
  const res = await fetch(`${API_BASE}/admin/emails/automation/config`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as Partial<AdminEmailAutomationConfig>;
  return {
    rules: Array.isArray(data.rules) ? data.rules : [],
    slaHours: typeof data.slaHours === 'number' ? data.slaHours : 24,
  };
}

export async function saveAdminEmailAutomationConfig(
  config: AdminEmailAutomationConfig,
): Promise<AdminEmailAutomationConfig> {
  const res = await fetch(`${API_BASE}/admin/emails/automation/config`, {
    method: 'PUT',
    headers: getAdminHeaders(),
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as Partial<AdminEmailAutomationConfig>;
  return {
    rules: Array.isArray(data.rules) ? data.rules : [],
    slaHours: typeof data.slaHours === 'number' ? data.slaHours : 24,
  };
}

export async function runAdminEmailAutomation(opts?: { includeSla?: boolean }): Promise<{
  success: boolean;
  updated: number;
  rulesEvaluated: number;
  ruleHits: number;
  slaFlagged: number;
}> {
  const res = await fetch(`${API_BASE}/admin/emails/automation/run`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ includeSla: opts?.includeSla !== false }),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as Partial<{
    success: boolean;
    updated: number;
    rulesEvaluated: number;
    ruleHits: number;
    slaFlagged: number;
  }>;
  return {
    success: data.success === true,
    updated: typeof data.updated === 'number' ? data.updated : 0,
    rulesEvaluated: typeof data.rulesEvaluated === 'number' ? data.rulesEvaluated : 0,
    ruleHits: typeof data.ruleHits === 'number' ? data.ruleHits : 0,
    slaFlagged: typeof data.slaFlagged === 'number' ? data.slaFlagged : 0,
  };
}

export async function archiveAdminLowRiskPromos(): Promise<{ archived: number }> {
  const res = await fetch(`${API_BASE}/admin/emails/automation/archive-low-risk-promos`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { archived?: number };
  return { archived: typeof data.archived === 'number' ? data.archived : 0 };
}

export async function getAdminEmailAutomationLogs(limit = 25): Promise<AdminEmailAutomationLogEntry[]> {
  const res = await fetch(
    `${API_BASE}/admin/emails/automation/logs?limit=${encodeURIComponent(String(limit))}`,
    { headers: getAdminHeaders() },
  );
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { logs?: AdminEmailAutomationLogEntry[] };
  return Array.isArray(data.logs) ? data.logs : [];
}

export async function runAdminEmailWebhookSelfTest(): Promise<{ success: boolean; id?: string }> {
  const res = await fetch(`${API_BASE}/admin/emails/diagnostics/webhook-self-test`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { success?: boolean; id?: string };
  return { success: data.success === true, id: data.id };
}

export async function pullAdminEmailsFromResend(limit = 25): Promise<{
  success: boolean;
  imported: number;
  skipped: number;
  errors: string[];
  message?: string;
}> {
  const res = await fetch(`${API_BASE}/admin/emails/received/pull-resend`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ limit }),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
    message: string;
    error: string;
  }>;
  if (!res.ok) {
    const combinedErrors = [
      ...(Array.isArray(data.errors) ? data.errors : []),
      typeof data.error === 'string' ? data.error : '',
    ].filter(Boolean);
    throw new Error(combinedErrors[0] || 'Failed to pull inbound emails from Resend');
  }
  return {
    success: data.success === true,
    imported: typeof data.imported === 'number' ? data.imported : 0,
    skipped: typeof data.skipped === 'number' ? data.skipped : 0,
    errors: Array.isArray(data.errors) ? data.errors : [],
    message: typeof data.message === 'string' ? data.message : undefined,
  };
}

/** KV + AI metadata merged server-side; supports folder + optional To: address filter */
export interface AdminEmailInboxDef {
  id: string;
  address: string;
  label: string;
}

export interface AdminEmailMeta {
  archived?: boolean;
  deleted?: boolean;
  read?: boolean;
  starred?: boolean;
  spamLikelihood?: 'low' | 'medium' | 'high';
  category?: string;
  aiReason?: string;
  classifiedAt?: string;
}

export type ResendEmailListItemEnriched = ResendEmailListItem & { meta?: AdminEmailMeta };

export async function getAdminEmailsReceivedEnriched(opts: {
  folder?: 'inbox' | 'spam' | 'archived' | 'trash' | 'all';
  to?: string;
}): Promise<{
  data: ResendEmailListItemEnriched[];
  has_more?: boolean;
  folder?: string;
  inboxes: AdminEmailInboxDef[];
}> {
  const folder = opts.folder || 'inbox';
  const q = new URLSearchParams({ folder });
  if (opts.to?.trim()) q.set('to', opts.to.trim());
  const res = await fetch(`${API_BASE}/admin/emails/received/enriched?${q}`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as {
    data?: ResendEmailListItemEnriched[];
    has_more?: boolean;
    folder?: string;
    inboxes?: AdminEmailInboxDef[];
  };
  return {
    data: Array.isArray(data.data) ? data.data : [],
    has_more: data.has_more,
    folder: data.folder,
    inboxes: Array.isArray(data.inboxes) ? data.inboxes : [],
  };
}

export async function getAdminEmailInboxDefinitions(): Promise<AdminEmailInboxDef[]> {
  const res = await fetch(`${API_BASE}/admin/emails/inboxes`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const j = (await res.json()) as { inboxes?: AdminEmailInboxDef[] };
  return Array.isArray(j.inboxes) ? j.inboxes : [];
}

export async function saveAdminEmailInboxDefinitions(
  inboxes: AdminEmailInboxDef[],
): Promise<AdminEmailInboxDef[]> {
  const res = await fetch(`${API_BASE}/admin/emails/inboxes`, {
    method: 'PUT',
    headers: getAdminHeaders(),
    body: JSON.stringify({ inboxes }),
  });
  if (!res.ok) throw new Error(await res.text());
  const j = (await res.json()) as { inboxes?: AdminEmailInboxDef[] };
  return Array.isArray(j.inboxes) ? j.inboxes : [];
}

export async function patchAdminReceivedEmailMeta(
  id: string,
  patch: { archived?: boolean; deleted?: boolean; read?: boolean; starred?: boolean },
): Promise<AdminEmailMeta> {
  const res = await fetch(`${API_BASE}/admin/emails/received/${encodeURIComponent(id)}/meta`, {
    method: 'PATCH',
    headers: getAdminHeaders(),
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await res.text());
  const j = (await res.json()) as { meta?: AdminEmailMeta };
  return j.meta || {};
}

export async function classifyAdminReceivedEmail(id: string): Promise<AdminEmailMeta> {
  const res = await fetch(`${API_BASE}/admin/emails/received/${encodeURIComponent(id)}/classify`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(await res.text());
  const j = (await res.json()) as { meta?: AdminEmailMeta };
  return j.meta || {};
}

export async function classifyAdminReceivedEmailBatch(
  limit?: number,
): Promise<{ classified: number; errors: string[] }> {
  const res = await fetch(`${API_BASE}/admin/emails/received/classify-unclassified`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ limit: limit ?? 12 }),
  });
  if (!res.ok) throw new Error(await res.text());
  const j = (await res.json()) as { classified?: number; errors?: string[] };
  return {
    classified: typeof j.classified === 'number' ? j.classified : 0,
    errors: Array.isArray(j.errors) ? j.errors : [],
  };
}

/** Admin: get single received email (full body). */
export interface ResendReceivedEmail extends ResendEmailListItem {
  html?: string;
  text?: string;
}

export async function getAdminEmailReceived(id: string): Promise<ResendReceivedEmail | null> {
  const res = await fetch(`${API_BASE}/admin/emails/received/${id}`, { headers: getAdminHeaders() });
  if (!res.ok) return null;
  return (await res.json()) as ResendReceivedEmail;
}

/** Admin: list sent emails. */
export interface ResendSentEmailItem {
  id: string;
  to: string[];
  from: string;
  created_at: string;
  subject: string;
  last_event?: string;
}

export async function getAdminEmailsSent(): Promise<{ data: ResendSentEmailItem[]; has_more?: boolean }> {
  const res = await fetch(`${API_BASE}/admin/emails/sent`, { headers: getAdminHeaders() });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { data?: ResendSentEmailItem[]; has_more?: boolean };
  return { data: Array.isArray(data.data) ? data.data : [], has_more: data.has_more };
}

/** Admin: get single sent email. */
export async function getAdminEmailSent(id: string): Promise<(ResendSentEmailItem & { html?: string; text?: string }) | null> {
  const res = await fetch(`${API_BASE}/admin/emails/sent/${id}`, { headers: getAdminHeaders() });
  if (!res.ok) return null;
  return (await res.json()) as ResendSentEmailItem & { html?: string; text?: string };
}

/** Admin: send email via Resend. */
export async function sendAdminEmail(params: {
  to: string | string[];
  from: string;
  subject: string;
  html?: string;
  text?: string;
  reply_to?: string;
  /** Base64 file contents (Resend attachment format). */
  attachments?: Array<{ filename: string; content: string }>;
}): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE}/admin/emails/send`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = (await res.json()) as { id: string };
  return data;
}

export interface InvestorAIPitchSendResult {
  success: boolean;
  investorId: string;
  to: string;
  from: string;
  replyTo?: string;
  subject: string;
  text: string;
  emailId: string;
  sentViaResend: boolean;
  queuedLocal: boolean;
  warning?: string;
}

export interface InvestorAIPitchDraft {
  success: boolean;
  investorId: string;
  templateId?: string;
  to: string;
  from: string;
  allowedFromOptions?: string[];
  replyTo?: string;
  subject: string;
  text: string;
  html?: string;
}

export async function getAdminInvestorAIPitchDraft(
  investorId: string,
  options?: {
    templateId?: string;
    iterationHint?: string;
    previousDraftText?: string;
    variationMode?: 'conservative' | 'balanced' | 'fresh';
  },
): Promise<InvestorAIPitchDraft> {
  const payload: Record<string, string> = {};
  if (typeof options?.templateId === 'string' && options.templateId.trim()) payload.templateId = options.templateId.trim();
  if (typeof options?.iterationHint === 'string' && options.iterationHint.trim()) payload.iterationHint = options.iterationHint.trim();
  if (typeof options?.previousDraftText === 'string' && options.previousDraftText.trim()) {
    payload.previousDraftText = options.previousDraftText;
  }
  if (typeof options?.variationMode === 'string' && options.variationMode.trim()) {
    payload.variationMode = options.variationMode;
  }
  const res = await fetch(`${API_BASE}/admin/investors/${encodeURIComponent(investorId)}/ai-email/draft`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(payload),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<InvestorAIPitchDraft> & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || 'Failed to generate AI investor email draft');
  }
  return {
    success: data.success === true,
    investorId: typeof data.investorId === 'string' ? data.investorId : investorId,
    templateId: typeof data.templateId === 'string' && data.templateId.trim().length > 0 ? data.templateId : 'cold_intro',
    to: typeof data.to === 'string' ? data.to : '',
    from: typeof data.from === 'string' ? data.from : '',
    allowedFromOptions: Array.isArray(data.allowedFromOptions)
      ? data.allowedFromOptions.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      : [],
    replyTo: typeof data.replyTo === 'string' ? data.replyTo : undefined,
    subject: typeof data.subject === 'string' ? data.subject : '',
    text: typeof data.text === 'string' ? data.text : '',
    html: typeof data.html === 'string' ? data.html : undefined,
  };
}

export async function sendAdminInvestorAIPitchEmail(
  investorId: string,
  overrides?: {
    subject?: string;
    text?: string;
    html?: string;
    from?: string;
    reply_to?: string;
    templateId?: string;
  },
): Promise<InvestorAIPitchSendResult> {
  const res = await fetch(`${API_BASE}/admin/investors/${encodeURIComponent(investorId)}/ai-email/send`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(overrides || {}),
  });
  const data = (await res.json().catch(() => ({}))) as Partial<InvestorAIPitchSendResult> & { error?: string };
  if (!res.ok) {
    throw new Error(data.error || 'Failed to generate and send AI investor email');
  }
  return {
    success: data.success === true,
    investorId: typeof data.investorId === 'string' ? data.investorId : investorId,
    to: typeof data.to === 'string' ? data.to : '',
    from: typeof data.from === 'string' ? data.from : '',
    replyTo: typeof data.replyTo === 'string' ? data.replyTo : undefined,
    subject: typeof data.subject === 'string' ? data.subject : '',
    text: typeof data.text === 'string' ? data.text : '',
    emailId: typeof data.emailId === 'string' ? data.emailId : '',
    sentViaResend: data.sentViaResend === true,
    queuedLocal: data.queuedLocal === true,
    warning: typeof data.warning === 'string' ? data.warning : undefined,
  };
}

export interface InvestorCustomEmailTemplate {
  id: string;
  name: string;
  subject: string;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export async function getAdminInvestorEmailTemplates(): Promise<InvestorCustomEmailTemplate[]> {
  const res = await fetch(`${API_BASE}/admin/investors/email-templates`, {
    headers: getAdminHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as { templates?: InvestorCustomEmailTemplate[]; error?: string };
  if (!res.ok) throw new Error(data.error || 'Failed to load investor email templates');
  return Array.isArray(data.templates) ? data.templates : [];
}

export async function saveAdminInvestorEmailTemplate(input: {
  id?: string;
  name: string;
  subject: string;
  text: string;
}): Promise<InvestorCustomEmailTemplate> {
  const res = await fetch(`${API_BASE}/admin/investors/email-templates`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as { template?: InvestorCustomEmailTemplate; error?: string };
  if (!res.ok || !data.template) throw new Error(data.error || 'Failed to save investor email template');
  return data.template;
}

export async function deleteAdminInvestorEmailTemplate(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/investors/email-templates/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getAdminHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error || 'Failed to delete investor email template');
}

/** Public: beta keys count for landing page (total, available, used). */
export async function getBetaKeysCount(): Promise<{ total: number; available: number; used: number }> {
  try {
    const res = await fetch(`${API_BASE}/beta/keys/count`, {
      headers: { Authorization: `Bearer ${publicAnonKey}` },
    });
    if (!res.ok) return { total: 0, available: 0, used: 0 };
    const data = (await res.json()) as { total?: number; available?: number; used?: number };
    return {
      total: typeof data.total === 'number' ? Math.max(0, data.total) : 0,
      available: typeof data.available === 'number' ? Math.max(0, data.available) : 0,
      used: typeof data.used === 'number' ? Math.max(0, data.used) : 0,
    };
  } catch {
    return { total: 0, available: 0, used: 0 };
  }
}

/** Public: current number of waitlist signups (for landing XX / 1000). */
export async function getWaitlistSignupCount(): Promise<number> {
  try {
    const res = await fetch(`${API_BASE}/waitlist/count`, {
      headers: { Authorization: `Bearer ${publicAnonKey}` },
    });
    if (!res.ok) return 0;
    const data = (await res.json()) as { total?: number };
    const n = typeof data.total === 'number' && Number.isFinite(data.total) ? Math.max(0, data.total) : 0;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('tension_waitlist_last_count', String(n));
      } catch {
        /* ignore */
      }
    }
    return n;
  } catch {
    if (typeof localStorage !== 'undefined') {
      try {
        const raw = localStorage.getItem('tension_waitlist_last_count');
        const parsed = raw ? parseInt(raw, 10) : 0;
        return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
      } catch {
        return 0;
      }
    }
    return 0;
  }
}

export interface AntiBotChallenge {
  token: string;
  difficulty: number;
  scope: string;
  expiresAt: number;
}

export interface AntiBotProof {
  token: string;
  nonce: string;
}

export async function getAntiBotChallenge(scope = 'beta_key_request'): Promise<AntiBotChallenge> {
  const res = await fetch(`${API_BASE}/anti-bot/challenge?scope=${encodeURIComponent(scope)}`, {
    headers: { Authorization: `Bearer ${publicAnonKey}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.token) {
    throw new Error(data?.error || 'Unable to initialize anti-bot challenge');
  }
  return {
    token: String(data.token),
    difficulty: Number(data.difficulty ?? 3),
    scope: String(data.scope ?? scope),
    expiresAt: Number(data.expiresAt ?? 0),
  };
}

function hasLeadingZeroNibbles(bytes: Uint8Array, requiredNibbles: number): boolean {
  const fullBytes = Math.floor(requiredNibbles / 2);
  const halfNibble = requiredNibbles % 2;

  for (let i = 0; i < fullBytes; i++) {
    if (bytes[i] !== 0) return false;
  }
  if (halfNibble) {
    const next = bytes[fullBytes];
    if ((next >> 4) !== 0) return false;
  }
  return true;
}

async function sha256Bytes(input: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(digest);
}

export async function solveAntiBotChallenge(challenge: AntiBotChallenge): Promise<AntiBotProof> {
  const maxAttempts = 2_000_000;
  for (let nonce = 0; nonce < maxAttempts; nonce++) {
    const hash = await sha256Bytes(`${challenge.token}:${nonce}`);
    if (hasLeadingZeroNibbles(hash, challenge.difficulty)) {
      return { token: challenge.token, nonce: String(nonce) };
    }
    if (nonce % 500 === 0) {
      // Yield to keep UI responsive on slower devices.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
  throw new Error('Anti-bot challenge failed to solve');
}

export async function joinWaitlist(
  email: string,
  name?: string,
  referralCode?: string,
  antiBotProof?: AntiBotProof
): Promise<{ position: number; id?: string; referralCode?: string; betaKeyIssued?: boolean; alreadyHadKey?: boolean }> {
  const res = await fetch(`${API_BASE}/waitlist/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${publicAnonKey}` },
    body: JSON.stringify({
      email: email.trim(),
      name: name?.trim() || null,
      referralCode: referralCode?.trim() || null,
      antiBotToken: antiBotProof?.token || null,
      antiBotNonce: antiBotProof?.nonce || null,
    }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || await res.text());
  return {
    position: data.position ?? 0,
    id: data.id,
    referralCode: data.referralCode,
    betaKeyIssued: Boolean(data.betaKeyIssued),
    alreadyHadKey: Boolean(data.alreadyHadKey),
  };
}

export async function sendWaitlistEmailUpdate(payload: {
  email: string;
  name?: string;
  referralCode?: string;
  position?: number | null;
  betaKeyIssued?: boolean;
  alreadyHadKey?: boolean;
}): Promise<{ success: boolean; skipped?: boolean; message?: string; id?: string }> {
  const res = await fetch(`${API_BASE}/send-waitlist-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${publicAnonKey}` },
    body: JSON.stringify({
      email: payload.email.trim(),
      name: payload.name?.trim() || null,
      referralCode: payload.referralCode?.trim() || null,
      position: typeof payload.position === 'number' ? payload.position : null,
      betaKeyIssued: payload.betaKeyIssued === true,
      alreadyHadKey: payload.alreadyHadKey === true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data && typeof data.error === 'string' && data.error) || 'Failed to send waitlist email update');
  }
  return {
    success: true,
    skipped: data?.skipped === true,
    message: typeof data?.message === 'string' ? data.message : undefined,
    id: typeof data?.id === 'string' ? data.id : undefined,
  };
}

export async function getWaitlistStatusByReferralCode(referralCode: string): Promise<{ position: number; totalWaitlist: number; referrals: number; email?: string }> {
  const res = await fetch(
    `${API_BASE}/waitlist/status?referralCode=${encodeURIComponent(referralCode)}`,
    { headers: { 'Authorization': `Bearer ${publicAnonKey}` } }
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// --- App settings (database: maintenance_mode, beta_mode, feature flags, etc.) ---

export interface AppSettings {
  tension_early_access_mode_enabled?: boolean;
  tension_beta_mode_enabled?: boolean;
  tension_maintenance_mode?: boolean;
  /** Master switch: MFA UI + APIs. Default off until product is ready. */
  tension_mfa_enabled?: boolean;
  /** When MFA is enabled, require step-up at sign-in for enrolled users. */
  tension_mfa_required_for_login?: boolean;
  /** When false, landing shows Coming Soon for non-admins. Default true. */
  tension_live_production?: boolean;
  /** When true, `/` shows waitlist splash instead of beta landing (when not signed in). */
  tension_waitlist_splash_enabled?: boolean;
  /** Consumer AI login snapshot feature flag (default true). */
  tension_consumer_login_snapshot_enabled?: boolean;
  /** Emergency stop for login snapshot generation (default false). */
  tension_consumer_login_snapshot_kill_switch?: boolean;
  /** Contract version expected by server snapshot endpoint. */
  tension_consumer_login_snapshot_schema_version?: string;
  /** SLO warning threshold for snapshot latency in milliseconds. */
  tension_consumer_login_snapshot_latency_warn_ms?: number;
  /** SLO failure threshold for snapshot latency in milliseconds. */
  tension_consumer_login_snapshot_latency_fail_ms?: number;
  /** SLO alert threshold for rolling failure rate (0-1). */
  tension_consumer_login_snapshot_failure_rate_alert_threshold?: number;
  /** Retention period (days) for stored per-user login snapshots. */
  tension_consumer_login_snapshot_retention_days?: number;
  /** Client proof-of-work difficulty for custom anti-bot checks (2-6). */
  tension_anti_bot_difficulty?: number;
  /** Rich feature-flag objects (Beta Command Center) or simple map; server accepts JSON. */
  beta_feature_flags?: Record<string, boolean> | unknown;
  beta_announcements?: any;
  beta_gamification_rules?: any;
  [key: string]: unknown;
}

export async function getAppSettings(): Promise<AppSettings> {
  try {
    const res = await fetch(`${API_BASE}/app/settings`, {
      headers: { 'Authorization': `Bearer ${publicAnonKey}` },
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data ?? {};
  } catch {
    return {};
  }
}

export async function setAppSettings(settings: Partial<AppSettings>): Promise<AppSettings> {
  const res = await fetch(`${API_BASE}/admin/settings`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return data.settings ?? {};
}

export interface ConsumerSnapshotSloEvent {
  id: string;
  ts: string;
  kind: string;
  latencyMs?: number | null;
  warnMs?: number | null;
  failMs?: number | null;
  threshold?: number | null;
  failureRate?: number | null;
  sampleSize?: number | null;
  reason?: string | null;
}

export interface ConsumerSnapshotSloSummary {
  total: number;
  byKind: Record<string, number>;
  p95LatencyMs: number | null;
  failureRateAlertCount: number;
}

export async function getAdminConsumerSnapshotSloTelemetry(limit = 50): Promise<{
  generatedAt: string;
  summary: ConsumerSnapshotSloSummary;
  recentEvents: ConsumerSnapshotSloEvent[];
}> {
  const res = await fetch(
    `${API_BASE}/admin/telemetry/consumer-snapshot-slo?limit=${encodeURIComponent(String(limit))}`,
    { headers: getAdminHeaders() },
  );
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return {
    generatedAt: data.generatedAt || new Date().toISOString(),
    summary: (data.summary || {
      total: 0,
      byKind: {},
      p95LatencyMs: null,
      failureRateAlertCount: 0,
    }) as ConsumerSnapshotSloSummary,
    recentEvents: Array.isArray(data.recentEvents) ? (data.recentEvents as ConsumerSnapshotSloEvent[]) : [],
  };
}

// --- Admin stats (Total Users, Active Sessions) ---

export interface AdminStats {
  totalUsers?: number;
  activeUsers?: number;
  activeSessions?: number;
  verifiedUsers?: number;
  bannedUsers?: number;
  newUsersLast7Days?: number;
}

export interface AdminSecurityEvent {
  type: string;
  route: string;
  client: string;
  ts: string;
  detail?: string;
  fingerprint?: string;
  suppressed?: boolean;
  suppressedUntil?: string | null;
  suppressionMode?: 'ack' | 'snooze' | null;
}

export interface AdminSecuritySummary {
  counts: { high: number; medium: number; low: number };
  highLast15m: number;
  totalLast15m: number;
  thresholds: { highEvents15m: number; totalEvents15m: number };
  spike: boolean;
  anomalies?: Array<{
    route: string;
    count15m: number;
    count60m: number;
    high15m: number;
    baseline15m: number;
    anomalyScore: number;
    anomalous: boolean;
  }>;
  playbooks?: Array<{
    eventType: string;
    route: string;
    severity: 'high' | 'medium' | 'low';
    title: string;
    actions: string[];
  }>;
}

export interface AdminSecurityAuditEntry {
  id: string;
  ts: string;
  action: 'ack' | 'snooze' | 'clear';
  fingerprint: string;
  until?: string | null;
  actor: string;
  client: string;
}

export async function getAdminStats(): Promise<AdminStats> {
  try {
    const res = await fetch(`${API_BASE}/admin/stats`, {
      headers: getAdminHeaders(),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return {
      totalUsers: data.totalUsers ?? data.total_users,
      activeUsers: data.activeUsers ?? data.active_users,
      activeSessions: data.activeSessions ?? data.active_sessions,
      verifiedUsers: data.verifiedUsers ?? data.verified_users,
      bannedUsers: data.bannedUsers ?? data.banned_users,
      newUsersLast7Days: data.newUsersLast7Days ?? data.new_users_last_7_days,
    };
  } catch {
    return {};
  }
}

export async function getAdminSecurityEvents(): Promise<AdminSecurityEvent[]> {
  try {
    const res = await fetch(`${API_BASE}/admin/security/events`, {
      headers: getAdminHeaders(),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.events) ? data.events : [];
  } catch {
    return [];
  }
}

export async function getAdminSecuritySummary(): Promise<{ events: (AdminSecurityEvent & { severity: 'high' | 'medium' | 'low' })[]; summary: AdminSecuritySummary } | null> {
  try {
    const res = await fetch(`${API_BASE}/admin/security/summary`, {
      headers: getAdminHeaders(),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data?.events) || !data?.summary) return null;
    return { events: data.events, summary: data.summary };
  } catch {
    return null;
  }
}

export async function acknowledgeAdminSecurityAlert(fingerprint: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/admin/security/alerts/ack`, {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ fingerprint }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function snoozeAdminSecurityAlert(fingerprint: string, minutes: number): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/admin/security/alerts/snooze`, {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ fingerprint, minutes }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function clearAdminSecurityAlert(fingerprint: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/admin/security/alerts/clear`, {
      method: 'POST',
      headers: getAdminHeaders(),
      body: JSON.stringify({ fingerprint }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function getAdminSecurityAudit(): Promise<AdminSecurityAuditEntry[]> {
  try {
    const res = await fetch(`${API_BASE}/admin/security/audit`, {
      headers: getAdminHeaders(),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.audit) ? data.audit : [];
  } catch {
    return [];
  }
}

// --- Store catalog (pricing / packs / Stripe IDs) ---

export interface StoreCatalogItem {
  id: string;
  slug: string;
  kind: string;
  name: string;
  subtitle?: string | null;
  description?: string | null;
  marketing_features: unknown;
  extended?: Record<string, unknown>;
  stripe_product_id?: string | null;
  stripe_price_id?: string | null;
  amount_cents?: number | null;
  currency?: string;
  billing_interval?: string | null;
  plan_tier?: string | null;
  billing_period_key?: string | null;
  pack_slugs?: string[] | null;
  checkout_metadata_type?: string | null;
  display_order?: number;
  active?: boolean;
  show_on_pricing?: boolean;
  show_on_packs_page?: boolean;
  badge_label?: string | null;
  founding_spots_cap?: number | null;
  ui_is_popular?: boolean;
  created_at?: string;
  updated_at?: string;
}

export async function getPublicStoreCatalog(): Promise<{
  items: StoreCatalogItem[];
  duplicateStripeWarnings?: string[];
  catalogError?: string;
  fallback?: boolean;
}> {
  const res = await fetch(`${API_BASE}/store/catalog`, {
    headers: { Authorization: `Bearer ${publicAnonKey}` },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load store catalog');
  return {
    items: Array.isArray(data.items) ? data.items : [],
    duplicateStripeWarnings: data.duplicateStripeWarnings,
    catalogError: data.catalogError,
    fallback: data.fallback,
  };
}

/** Founding pass urgency (sold count, cap, remaining). Uses catalog cap; sold from API if available. */
export async function getStoreUrgency(): Promise<{
  foundingSold: number;
  foundingCap: number;
  foundingRemaining: number;
}> {
  try {
    const res = await fetch(`${API_BASE}/store/urgency`, {
      headers: { Authorization: `Bearer ${publicAnonKey}` },
    });
    if (res.ok) {
      const data = (await res.json()) as { foundingSold?: number; foundingCap?: number; foundingRemaining?: number };
      const foundingSold = typeof data.foundingSold === 'number' ? data.foundingSold : 0;
      const foundingCap = typeof data.foundingCap === 'number' ? data.foundingCap : 100;
      const foundingRemaining = typeof data.foundingRemaining === 'number'
        ? data.foundingRemaining
        : Math.max(0, foundingCap - foundingSold);
      return { foundingSold, foundingCap, foundingRemaining };
    }
  } catch {
    /* fallback below */
  }
  try {
    const catalog = await getPublicStoreCatalog();
    const foundingRow = catalog.items?.find((i) => i.kind === 'founding' && i.active !== false);
    const foundingCap = foundingRow?.founding_spots_cap ?? 100;
    const foundingSold = 0;
    return { foundingSold, foundingCap, foundingRemaining: Math.max(0, foundingCap - foundingSold) };
  } catch {
    return { foundingSold: 0, foundingCap: 100, foundingRemaining: 100 };
  }
}

export async function getAdminStoreCatalog(): Promise<{
  items: StoreCatalogItem[];
  dbError?: string | null;
  duplicateStripeWarnings?: string[];
}> {
  const res = await fetch(`${API_BASE}/admin/store/catalog`, {
    headers: getAdminHeaders(),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to load admin catalog');
  return {
    items: Array.isArray(data.items) ? data.items : [],
    dbError: data.dbError ?? null,
    duplicateStripeWarnings: data.duplicateStripeWarnings ?? [],
  };
}

export async function createStoreCatalogItem(
  body: Record<string, unknown>,
): Promise<{ item: StoreCatalogItem; github?: unknown }> {
  const res = await fetch(`${API_BASE}/admin/store/catalog`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Create failed');
  return { item: data.item, github: data.github };
}

export async function updateStoreCatalogItem(
  id: string,
  body: Record<string, unknown>,
): Promise<{ item: StoreCatalogItem; github?: unknown }> {
  const res = await fetch(`${API_BASE}/admin/store/catalog/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: getAdminHeaders(),
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Update failed');
  return { item: data.item, github: data.github };
}

export async function deleteStoreCatalogItem(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/admin/store/catalog/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getAdminHeaders(),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || 'Delete failed');
  }
}

export async function syncStoreCatalogToGitHub(path?: string): Promise<unknown> {
  const res = await fetch(`${API_BASE}/admin/store/catalog/sync-github`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify(path ? { path } : {}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error((data as { error?: string }).error || 'GitHub sync failed');
  return data;
}

/** Bulk-create Stripe Product + Price per catalog row and persist new IDs (e.g. after wiping Stripe). */
export interface StoreCatalogStripeSyncResult {
  ok: boolean;
  dry_run: boolean;
  summary: {
    total_rows: number;
    stripe_created_or_previewed: number;
    skipped: number;
    failed: number;
  };
  results: Array<Record<string, unknown>>;
}

export async function syncStoreCatalogStripe(opts?: {
  dryRun?: boolean;
}): Promise<StoreCatalogStripeSyncResult> {
  const res = await fetch(`${API_BASE}/admin/store/catalog/sync-stripe`, {
    method: 'POST',
    headers: getAdminHeaders(),
    body: JSON.stringify({ dry_run: opts?.dryRun === true }),
  });
  const data = (await res.json()) as StoreCatalogStripeSyncResult & { error?: string };
  if (!res.ok) throw new Error(data.error || 'Stripe catalog sync failed');
  return data;
}

export interface AdminStoreBillingDiagnostics {
  checkedAt: string;
  stripeConfigured: boolean;
  catalogDbError?: string | null;
  catalogSummary: {
    totalRows: number;
    activeRows: number;
    pricingVisibleRows: number;
    packsVisibleRows: number;
  };
  unresolvedActiveStripeRows: Array<{ slug: string; kind: string; reason: string }>;
  duplicateStripeWarnings: string[];
  subscriptionCoverage: {
    premiumMonthly: boolean;
    premiumAnnual: boolean;
    proMonthly: boolean;
    proAnnual: boolean;
  };
  checkoutReadiness: {
    subscriptionRouteReady: boolean;
    packRouteReady: boolean;
  };
  stripePriceChecks: Array<{ slug: string; priceId: string; ok: boolean; error?: string }>;
}

export async function getAdminStoreBillingDiagnostics(): Promise<AdminStoreBillingDiagnostics> {
  const res = await fetch(`${API_BASE}/admin/store/billing-diagnostics`, {
    headers: getAdminHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to run billing diagnostics');
  return {
    checkedAt: typeof data.checkedAt === 'string' ? data.checkedAt : new Date().toISOString(),
    stripeConfigured: data.stripeConfigured === true,
    catalogDbError: typeof data.catalogDbError === 'string' ? data.catalogDbError : null,
    catalogSummary: {
      totalRows: Number(data?.catalogSummary?.totalRows || 0),
      activeRows: Number(data?.catalogSummary?.activeRows || 0),
      pricingVisibleRows: Number(data?.catalogSummary?.pricingVisibleRows || 0),
      packsVisibleRows: Number(data?.catalogSummary?.packsVisibleRows || 0),
    },
    unresolvedActiveStripeRows: Array.isArray(data.unresolvedActiveStripeRows) ? data.unresolvedActiveStripeRows : [],
    duplicateStripeWarnings: Array.isArray(data.duplicateStripeWarnings) ? data.duplicateStripeWarnings : [],
    subscriptionCoverage: {
      premiumMonthly: data?.subscriptionCoverage?.premiumMonthly === true,
      premiumAnnual: data?.subscriptionCoverage?.premiumAnnual === true,
      proMonthly: data?.subscriptionCoverage?.proMonthly === true,
      proAnnual: data?.subscriptionCoverage?.proAnnual === true,
    },
    checkoutReadiness: {
      subscriptionRouteReady: data?.checkoutReadiness?.subscriptionRouteReady === true,
      packRouteReady: data?.checkoutReadiness?.packRouteReady === true,
    },
    stripePriceChecks: Array.isArray(data.stripePriceChecks) ? data.stripePriceChecks : [],
  };
}

// --- Client error reporting + admin error logs ---
// Backend contract (Supabase Edge Function make-server-ba6cedc4):
//   POST /errors  Body: ClientErrorLog (JSON). Auth: Bearer anon. Stores log for admin view.
//   GET /admin/errors  Returns { logs: ClientErrorLog[] }. Auth: X-Session-Token (or X-User-Token for admin users).

export interface ClientErrorLog {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  componentStack?: string;
  userAgent: string;
  url: string;
  userId?: string;
  level?: string;
  sessionId?: string;
  context?: Record<string, unknown>;
  tags?: string[];
}

export async function reportError(log: ClientErrorLog): Promise<void> {
  try {
    await fetch(`${API_BASE}/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${publicAnonKey}` },
      body: JSON.stringify(log),
    });
  } catch {
    // Fire-and-forget; do not throw
  }
}

export async function getAdminErrorLogs(): Promise<ClientErrorLog[]> {
  try {
    const res = await fetch(`${API_BASE}/admin/errors`, {
      headers: getAdminHeaders(),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.logs) ? data.logs : [];
  } catch {
    return [];
  }
}
